import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

type AcessoComponente = {
  id?: string;
  nome: string;
};

type Row = {
  id: string;
  nome: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: AcessoComponente[];
  onComponentesChange?: (componentes: AcessoComponente[]) => void;
  bancoDeDadosId?: string;
};

const convertToRows = (componentes: AcessoComponente[]): Row[] => {
  return (componentes || []).map((item, index) => ({
    id: item.id || `temp-${Date.now()}-${index}`,
    nome: item.nome,
  }));
};

const convertToComponentes = (rows: Row[]): AcessoComponente[] => {
  return (rows || [])
    .filter((row) => {
      // Filtrar apenas rows válidos: não está sendo editado, não é novo, e tem nome
      const isValid = !row.isNew && !row.isEditing && row.nome?.trim();
      return isValid;
    })
    .map((row) => ({
      // Se o ID começa com 'temp-', não incluir (será gerado pelo backend)
      id: row.id.startsWith('temp-') ? undefined : row.id,
      nome: row.nome.trim(),
    }));
};

export const ListaAcessoComponentes: React.FC<Props> = ({
  initialRows = [],
  onComponentesChange,
  bancoDeDadosId,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingValues, setEditingValues] = useState<{
    [key: string]: {
      nome: string;
    };
  }>({});

  useEffect(() => {
    if (initialRows && initialRows.length > 0) {
      const newRows = convertToRows(initialRows);
      setRows(newRows);
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;

    setRows(rows?.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
    setEditingValues({
      ...editingValues,
      [id]: {
        nome: row.nome,
      },
    });
  };

  const handleCancelEdit = (id: string) => {
    setRows(
      rows
        ?.map((r) => {
          if (r.id === id) {
            if (r.isNew) {
              return null;
            }
            return { ...r, isEditing: false };
          }
          return r;
        })
        .filter(Boolean) as Row[]
    );

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);
  };

  const handleSaveEdit = (id: string) => {
    const editingValue = editingValues[id];
    if (!editingValue || !editingValue.nome?.trim()) {
      return;
    }

    const newRows = rows?.map((r) =>
      r.id === id
        ? {
          ...r,
          id: r.isNew
            ? `componente-${Date.now()}-${Math.random()
              .toString(36)
              .substr(2, 9)}`
            : r.id,
          nome: editingValue.nome.trim(),
          isEditing: false,
          isNew: false,
        }
        : r
    );

    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onComponentesChange) {
      const componentesData = convertToComponentes(newRows);
      onComponentesChange(componentesData);
    }
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    const newEditingValues = { ...editingValues };
    delete newEditingValues[id];
    setEditingValues(newEditingValues);

    if (onComponentesChange) {
      const componentesData = convertToComponentes(newRows);
      onComponentesChange(componentesData);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: `temp-new-${Date.now()}`,
      nome: '',
      isEditing: true,
      isNew: true,
    };

    setRows([...rows, newRow]);
    setEditingValues({
      ...editingValues,
      [newRow.id]: {
        nome: '',
      },
    });
  };

  const handleEditingValueChange = (id: string, field: string, value: any) => {
    setEditingValues({
      ...editingValues,
      [id]: {
        ...editingValues[id],
        [field]: value,
      },
    });
  };

  const canSave = (id: string) => {
    const editingValue = editingValues[id];
    return editingValue && editingValue.nome?.trim();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={2}>
      <Button
        variant="contained"
        color="primary"
        size="small"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Componente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhum acesso a componente cadastrado
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    {row.isEditing ? (
                      <>
                        <Tooltip title="Salvar">
                          <IconButton
                            size="small"
                            onClick={() => handleSaveEdit(row.id)}
                            color="primary"
                            disabled={!canSave(row.id)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancelar">
                          <IconButton
                            size="small"
                            onClick={() => handleCancelEdit(row.id)}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(row.id)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(row.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {row.isEditing ? (
                      <TextField
                        fullWidth
                        size="small"
                        value={editingValues[row.id]?.nome || ''}
                        onChange={(e) =>
                          handleEditingValueChange(
                            row.id,
                            'nome',
                            e.target.value
                          )
                        }
                        variant="outlined"
                        placeholder="Ex: UserController, DatabaseService"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            if (canSave(row.id)) {
                              handleSaveEdit(row.id);
                            }
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            handleCancelEdit(row.id);
                          }
                        }}
                      />
                    ) : (
                      row.nome
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
